import React, { useCallback, useEffect, useRef, useState } from "react";
import SuperlinkMarketSearchResult from "./MarketSearchResult";
import { MarketBasketItem, Price } from "./MarketModal";
import axios from "axios";
import Spinner from "../Spinner";
import { backbutton, poweredBy, searchButton } from "./svgs";
import MarketCurrencySelector from "./MarketCurrencySelector";
import SuperlinkMarketSearchDidYouKnow from "./MarketSearchDidYouKnow";

interface MarketSearchPageProps {
  query: string;
  userInfo: any;
  markets: string[];
  apiUrl: any;
  result: MarketBasketItem[];
  currencies: string[];
  currency: string;
  setCurrency: (currency: string) => void;
  addToBasket: (item: MarketBasketItem) => void;
  setResult: (value: React.SetStateAction<MarketBasketItem[]>) => void;
  setQuery: (query: string) => void;
  setHeight: (height: any) => void;
  back: () => void;
  currencyEstimates: [
    {
      symbol: string;
      name: string;
      exhangeRate: number;
    }
  ];
  seconds: number;
}

export const priceConversion = (price: Price, exchangeRate: number): Price => {
  return {
    ens_bridge: (price.ens_bridge || 0) * exchangeRate,
    ens_rent: (price.ens_rent || 0) * exchangeRate,
    ens_setup_gas: (price.ens_setup_gas || 0) * exchangeRate,
    renewal: (price.renewal || 0) * exchangeRate,
    setup: (price.setup || 0) * exchangeRate,
  };
};

const MarketSearchPage: React.FC<MarketSearchPageProps> = ({
  query,
  userInfo,
  markets,
  apiUrl,
  result,
  currencyEstimates,
  seconds,
  currencies,
  currency,
  setCurrency,
  addToBasket,
  setQuery,
  setResult,
  setHeight,
  back,
}) => {
  const [searchFocus, setSearchFocus] = useState(false);
  const [loading, setLoading] = useState(false);
  const [basket, setBasket] = useState<MarketBasketItem>();
  const [searchCompleted, setSearchCompleted] = useState(false);

  // Step 1: Create a ref to attach to the div
  const divRef = useRef<HTMLDivElement>(null);

  const [isBottom, setIsBottom] = useState(false);

  const handleScroll = useCallback(() => {
    if (divRef.current) {
      const { scrollTop, scrollHeight, clientHeight } = divRef.current;
      // Check if we have reached the bottom and the event has not already been handled
      const isAtBottom = scrollTop + clientHeight >= scrollHeight/2;

      if (isAtBottom && !isBottom) {
        // Set the flag to true to indicate that the bottom has been reached
        setIsBottom(true);
        // Perform actions such as loading more data
        if (query == "") {
          fetchSuggestions(userInfo?.fullName?.toLowerCase() || "nora miller");
          return;
        }
        fetchSuggestions(query.toLowerCase());
      } else if (!isAtBottom && isBottom) {
        // Reset the flag when scrolled away from the bottom
        setIsBottom(false);
      }
    }
  }, [isBottom]);

  // Step 4: Attach the scroll event handler to the div using the ref
  useEffect(() => {
    const div = divRef.current;
    if (div) {
      div.addEventListener("scroll", handleScroll);

      // Step 5: Clean up
      return () => {
        div.removeEventListener("scroll", handleScroll);
      };
    }
  }, [handleScroll]); // Re-run if handleScroll changes

  let widthStyle = "100%"; // default

  if (navigator.userAgent.includes("Firefox")) {
    widthStyle = "-moz-available";
  } else if (
    navigator.userAgent.includes("Chrome") ||
    navigator.userAgent.includes("Safari")
  ) {
    widthStyle = "-webkit-fill-available";
  } else {
    widthStyle = "fill-available";
  }

  useEffect(() => {
    setHeight(700);
  }, []);

  const pageStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    gap: 20,
    justifyContent: "top",
    alignItems: "center",
    height: "100%",
    width: widthStyle,
  };

  const topStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "column",
    gap: 16,
    width: widthStyle,
    zIndex: 2,
    paddingTop: 24,
    paddingRight: 24,
    paddingLeft: 24,
  };

  const bottomStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    justifyContent: "space-between",
    alignItems: "center",
    alignContent: "center",
    height: "100%",
    width: widthStyle,
    borderTop: "1px solid rgba(0, 0, 0, 0.1)",
    overflow: "scroll",
  };

  const formStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "center",
    alignItems: "center",
    flexDirection: "row",
    gap: 10,
    width: widthStyle,
  };

  const searchButtonStyle: React.CSSProperties = {
    flex: "0 0 auto",
    justifyContent: "center",
    cursor: "pointer",
    alignItems: "center",
    paddingTop: 4,
    width: 48,
    height: 48,
    background: "linear-gradient(108.64deg, #854EFB 7.51%, #8A88FF 98.44%)",
    boxShadow:
      "0px 122.973px 98.3784px rgba(0, 0, 0, 0.07), 0px 51.3752px 41.1001px rgba(0, 0, 0, 0.0503198), 0px 27.4676px 21.9741px rgba(0, 0, 0, 0.0417275), 0px 15.3981px 12.3185px rgba(0, 0, 0, 0.035), 0px 8.17783px 6.54226px rgba(0, 0, 0, 0.0282725), 0px 3.40298px 2.72238px rgba(0, 0, 0, 0.0196802)",
    borderRadius: 12,
    border: "1.5px solid #8A88FF",
    color: "#FFFFFF",
  };

  const backButtonStyle: React.CSSProperties = {
    justifyContent: "center",
    cursor: "pointer",
    alignItems: "center",
    paddingTop: 4,
    width: 32,
    height: 32,
    background: "rgba(0, 0, 0, 0.05)",
    borderRadius: 12,
    border: "none",
    color: "#000000",
  };

  const searchInputStyle = (hasFocus: boolean): React.CSSProperties => ({
    display: "flex",
    height: 48,
    background: "#FFFFFF",
    borderColor: "#FFFFFF",
    outline: "none",
    border: hasFocus ? "1.5px solid #8A88FF" : "1.5px solid #FFFFFF",
    fontFamily: "GT Planar Regular",
    fontSize: "16px",
    boxShadow:
      "0px 109px 230px rgba(38, 14, 110, 0.2), 0px 45.5376px 96.0885px rgba(38, 14, 110, 0.143771), 0px 24.3466px 51.3735px rgba(38, 14, 110, 0.119221), 0px 13.6485px 28.7996px rgba(38, 14, 110, 0.1), 0px 7.24861px 15.2952px rgba(38, 14, 110, 0.0807786), 0px 3.01631px 6.36469px rgba(38, 14, 110, 0.0562291)",
    borderRadius: 12,
    width: widthStyle,
    minWidth: "30px",
    paddingLeft: 10,
    margin: 0,
  });

  const resultStyle: React.CSSProperties = {
    display: "flex",
    flexDirection: "column",
    width: "100%",
  };

  const spinnerStyle: React.CSSProperties = {
    display: "flex",
    justifyContent: "top",
    flexDirection: "column",
    alignItems: "center",
    alignContent: "center",
    padding: 50,
    height: "35%",
    width: "100%",
  };

  const fetchData = (q: string) => {
    if (loading) return;
    console.log("search!!")
    setLoading(true);
    setSearchCompleted(false);
    setResult([]);

    const query = encodeURI(q.replace(/\./g, " "));
    const headers = {
      "Content-Type": "application/json",
    };
    axios
      .get(`${apiUrl}/v1/market/search/${query}`, { headers })
      .then((response) => {
        var listings: MarketBasketItem[] = response.data.marketListings;
        setResult(listings.filter((l) => markets.indexOf(l.nameService) > -1));
      })
      .catch((error) => {
        console.error("An error occurred while fetching data:", error);
      })
      .finally(() => {
        setLoading(false);
      });
  };

  const fetchSuggestions = (q: string) => {
    if (loading || searchCompleted) return;
    console.log("suggestions!")
    setLoading(true);

    const query = encodeURI(q.replace(/\./g, " "));
    const headers = {
      "Content-Type": "application/json",
    };

    axios
      .get(`${apiUrl}/v1/market/suggest/${query}`, { headers })
      .then((response) => {
        var listings: MarketBasketItem[] = response.data.marketListings;

        setResult((prevResult) =>
          prevResult.concat(
            listings
              .filter((l) => markets.indexOf(l.nameService) > -1)
              .filter((x) => !prevResult.includes(x))
          )
        );
      })
      .catch((error) => {
        console.error("An error occurred while fetching data:", error);
      })
      .finally(() => {
        setLoading(false);
        setSearchCompleted(true);
      });
  };

  useEffect(() => {
    if (result.length > 0) {
      return;
    }

    if (query == "") {
      fetchData(userInfo?.fullName?.toLowerCase() || "nora miller");
      return;
    }

    fetchData(query.toLowerCase());
  }, []);

  useEffect(() => {
    const expire = localStorage.getItem("checkoutIdExpire");
    var currentEpochTime = new Date().getTime();

    const b = localStorage.getItem("basket");
    if (b && b != "" && b != "undefined" && Number(expire) > currentEpochTime) {
      const bb: MarketBasketItem = JSON.parse(b);
      setBasket(bb);
    }
  }, []);

  const handleInputChange = async (e: any) => {
    e.preventDefault();
    setQuery(e.target.value);
  };

  const handleSubmit = (e: any) => {
    e.preventDefault();
    if (query == "") {
      fetchData(userInfo?.fullName?.toLowerCase() || "nora miller");
      return;
    }
    fetchData(query.toLowerCase());
  };

  return (
    <div style={pageStyle}>
      <div
        style={{
          ...topStyle,
          flexDirection: currencies.length > 0 ? "column" : "row",
          gap: currencies.length > 0 ? 16 : 10,
        }}
      >
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "space-between",
            alignContent: "center",
            alignItems: "center",
            width: currencies.length > 0 ? "100%" : "auto",
            gap: currencies.length > 0 ? 10 : 0,
          }}
        >
          {currencies.length > 0 && (
            <>
              <button onClick={back} style={backButtonStyle}>
                {" "}
                {backbutton}
              </button>
              <MarketCurrencySelector
                currencyEstimates={currencyEstimates}
                currencies={currencies}
                currency={currency}
                setCurrency={setCurrency}
                seconds={seconds}
              />
            </>
          )}
        </div>

        <form onSubmit={handleSubmit} style={formStyle}>
          {currencies.length == 0 && (
            <button onClick={back} style={backButtonStyle}>
              {" "}
              {backbutton}
            </button>
          )}
          <input
            type="text"
            onChange={handleInputChange}
            placeholder={userInfo.fullName || "Nora Miller"}
            value={query}
            style={searchInputStyle(searchFocus)}
            onFocus={() => {
              setSearchFocus(true);
            }}
            onBlur={() => setSearchFocus(false)}
          />
          <button type="submit" style={searchButtonStyle}>
            <span>
              {searchButton}
            </span>
          </button>
        </form>
      </div>

      <div style={bottomStyle} ref={divRef}>
        <div style={resultStyle}>
          {result &&
            result.map((r, index) => {
              if (index == 3 && markets.includes("icann")) {
                return (
                  <>
                    <SuperlinkMarketSearchResult
                      addToBasket={addToBasket}
                      key={index}
                      domain={r.domain}
                      nameService={r.nameService}
                      price={r.price}
                      currency={currency}
                      region={"US"}
                      features={r.features}
                      currencyEstimates={currencyEstimates}
                    />
                    <SuperlinkMarketSearchDidYouKnow />
                  </>
                );
              }
              return (
                <SuperlinkMarketSearchResult
                  addToBasket={addToBasket}
                  key={index}
                  domain={r.domain}
                  nameService={r.nameService}
                  price={r.price}
                  currency={currency}
                  region={"US"}
                  features={r.features}
                  currencyEstimates={currencyEstimates}
                />
              );
            })}
        </div>
        {loading && (
          <div style={spinnerStyle}>
            <Spinner width="80px" height="80px" border="10px" />
          </div>
        )}
        <div
          style={{
            display: "flex",
            flexDirection: "row",
            justifyContent: "center",
            paddingTop: 20,
            paddingBottom: 25,
            width: "100%",
            alignContent: "center",
            alignItems: "center",
          }}
        >
          {poweredBy}
        </div>
      </div>
    </div>
  );
};

export default MarketSearchPage;
